import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { BlogSnapshot } from "@shared/blog";

import type { BlogLinkSurface } from "@/lib/blog/tracking";

/**
 * Per-post context read by the MDX components (BlogAnchor, DataBlock, StoreCard, BuyerQuote).
 *
 * `firstDataBlockId` is computed server-side in the post's +data from the raw MDX, so DataBlock can
 * show the methodology line on the first block only, identically on server and client. Never
 * replace it with a render-time mutable ref or module state: that would diverge between SSR and
 * hydration.
 */
export type BlogPostContextValue = {
  slug: string;
  snapshot: BlogSnapshot | null;
  surface: BlogLinkSurface;
  firstDataBlockId: string | null;
};

// Safe default for renders with no provider mounted (SSR of a non-post surface, tests), mirroring
// the useAuth() server default. Carries all four fields so consumers never read undefined.
const DEFAULT_VALUE: BlogPostContextValue = {
  slug: "",
  snapshot: null,
  surface: "blog_post",
  firstDataBlockId: null,
};

const BlogPostContext = createContext<BlogPostContextValue>(DEFAULT_VALUE);

export function BlogPostProvider({
  value,
  children,
}: {
  value: BlogPostContextValue;
  children: ReactNode;
}) {
  return <BlogPostContext.Provider value={value}>{children}</BlogPostContext.Provider>;
}

export function useBlogPost(): BlogPostContextValue {
  return useContext(BlogPostContext);
}
